/*
 * The details column of an AI chat (S12 §6.2): three views in one group, headed "Claude's work", with
 * the tabs Tasks · Workflow · Context. Each is opened by a request naming its view and the chat's key
 * (core's details column opens nothing by being registered), and the tabs keep those params, so a
 * reader moves between them without the page's help.
 *
 * `panelRequest` is the one way a door builds such a request — the run line's Open and Tasks, the
 * workflow card's Open, the status bar's context meter, a `?panel=` in the address — so the params a
 * view reads are named in one place.
 *
 * This file is statically reachable from the module's entry (`../index.ts`): it holds ids, keys and
 * `lazy()` views, and no view code.
 */

import { lazy } from "react"
import type { DetailsView } from "../../../core/modules/registry.ts"
import type { DetailsRequest } from "../../../core/shell/index.ts"

/** The group all three views share, and the heading of the column. */
export const PANEL_GROUP = "aichat.panel"

/** The panel's tabs, in strip order, with the details view each is. */
export const PANEL_TABS = {
	tasks: "aichat.tasks",
	workflow: "aichat.workflow",
	context: "aichat.context",
} as const

export type PanelTab = keyof typeof PANEL_TABS

/** The order of the strip; the ids are the `?panel=` words of the chat page's address. */
export const PANEL_TAB_ORDER: readonly PanelTab[] = ["tasks", "workflow", "context"]

/** What a view of the panel reads from its request. */
export interface PanelParams {
	/** The chat's key. */
	key: string
	/** The workflow run the Workflow tab shows (`?run=`); the newest when absent. */
	run?: string
	/** The agent drilled down into (`?agent=`, its src): a workflow's agent, or a helper in Tasks. */
	agent?: string
}

/** The request that opens one tab of a chat's panel, on a run and an agent where they are named. */
export function panelRequest(
	key: string,
	tab: PanelTab,
	run?: string,
	agent?: string,
): DetailsRequest {
	return {
		view: PANEL_TABS[tab],
		params: {
			key,
			...(run === undefined ? {} : { run }),
			...(agent === undefined ? {} : { agent }),
		},
	}
}

/** A request's params as the panel's views read them; an unknown shape reads as no chat. */
export function panelParams(params: Record<string, string | number>): PanelParams {
	const run = params["run"]
	const agent = params["agent"]
	return {
		key: String(params["key"] ?? ""),
		...(run === undefined ? {} : { run: String(run) }),
		...(agent === undefined ? {} : { agent: String(agent) }),
	}
}

const TasksView = lazy(async () => ({ default: (await import("./TasksView.tsx")).TasksView }))
const WorkflowView = lazy(async () => ({
	default: (await import("./WorkflowView.tsx")).WorkflowView,
}))
const ContextView = lazy(async () => ({ default: (await import("./ContextView.tsx")).ContextView }))

/**
 * The three views for the module's `detailsViews` slot. The Workflow tab is offered in every chat,
 * whether or not a workflow has run: the strip then stays the same three tabs under the reader's
 * pointer, and a chat without a run says in one sentence what a workflow is.
 */
export const aichatDetailsViews: DetailsView[] = [
	{
		id: PANEL_TABS.tasks,
		titleKey: "aichat.panel.tasks",
		groupTitleKey: "aichat.panel.title",
		tabLabelKey: "aichat.panel.tasks",
		View: TasksView,
		group: PANEL_GROUP,
		order: 10,
	},
	{
		id: PANEL_TABS.workflow,
		titleKey: "aichat.panel.workflow",
		groupTitleKey: "aichat.panel.title",
		tabLabelKey: "aichat.panel.workflow",
		View: WorkflowView,
		group: PANEL_GROUP,
		order: 20,
	},
	{
		id: PANEL_TABS.context,
		titleKey: "aichat.panel.context",
		groupTitleKey: "aichat.panel.title",
		tabLabelKey: "aichat.panel.context",
		View: ContextView,
		group: PANEL_GROUP,
		order: 30,
	},
]
